import { useState, useEffect, useCallback, useRef } from 'react';
import type { LifecycleStage, LifecycleWatchSubstatus } from '@/constants/lifecycleStages';
import { LIFECYCLE_WATCH_SUBSTATUS_OPTIONS } from '@/constants/lifecycleStages';

const VALID_WATCH_SUBS: LifecycleWatchSubstatus[] =
  LIFECYCLE_WATCH_SUBSTATUS_OPTIONS.map(o => o.value) as LifecycleWatchSubstatus[];


const VALID_STAGES: LifecycleStage[] = ['yet_transferred', 'warranty', 'watch', 'maintenance'];
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Maintenance filters - includes projectMark, projectRoleUserIds, projectRoleTypes
export interface MaintenanceFiltersData {
  searchQuery: string;
  projectGroup: string;
  projectType: string[];
  teamLead: string;
  company: string;
  projectMark: string;
  projectRoleUserIds: string[];
  projectRoleTypes: string[];
  lifecycleStage: LifecycleStage | '';
  watchSubstatus: LifecycleWatchSubstatus | '';
  handoverDateFrom: string; // YYYY-MM-DD or ''
  handoverDateTo: string;   // YYYY-MM-DD or ''
  contractYear: string[];
}


const DEFAULT_MAINTENANCE_FILTERS: MaintenanceFiltersData = {
  searchQuery: '',
  projectGroup: '',
  projectType: [],
  teamLead: '',
  company: '',
  projectMark: '',
  projectRoleUserIds: [],
  projectRoleTypes: [],
  lifecycleStage: '',
  watchSubstatus: '',
  handoverDateFrom: '',
  handoverDateTo: '',
  contractYear: [],
};



const MAINTENANCE_FILTERS_STORAGE_KEY = 'maintenance_project_filters';

/**
 * Hook for managing maintenance page filter state with localStorage persistence
 * Namespace isolated from regular project filters
 */
export const useMaintenanceFiltersState = () => {
  const [filters, setFilters] = useState<MaintenanceFiltersData>(() => {
    try {
      const stored = localStorage.getItem(MAINTENANCE_FILTERS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return validateMaintenanceFilters(parsed);
      }
    } catch (error) {
      console.warn('Failed to load maintenance filters from storage:', error);
    }
    return DEFAULT_MAINTENANCE_FILTERS;
  });

  // Debounced localStorage save
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(MAINTENANCE_FILTERS_STORAGE_KEY, JSON.stringify(filters));
      } catch (error) {
        console.warn('Failed to save maintenance filters to storage:', error);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [filters]);

  const updateFilters = useCallback((newFilters: MaintenanceFiltersData) => {
    const validated = validateMaintenanceFilters(newFilters);
    setFilters(validated);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_MAINTENANCE_FILTERS);
  }, []);

  const updateSingleFilter = useCallback((key: keyof MaintenanceFiltersData, value: string | string[]) => {
    setFilters(prev => validateMaintenanceFilters({ ...prev, [key]: value }));
  }, []);


  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.projectType.length > 0) count++;
    if (filters.company) count++;
    if (filters.projectGroup) count++;
    if (filters.teamLead) count++;
    if (filters.projectMark) count++;
    if (filters.projectRoleUserIds.length > 0) count++;
    if (filters.projectRoleTypes.length > 0) count++;
    if (filters.watchSubstatus) count++;
    if (filters.contractYear.length > 0) count++;
    return count;
  }, [filters]);



  const isDefaultState = getActiveFiltersCount() === 0;

  return {
    filters,
    updateFilters,
    clearFilters,
    updateSingleFilter,
    getActiveFiltersCount,
    isDefaultState
  };
};

export function validateMaintenanceFilters(filters: any): MaintenanceFiltersData {
  const stage = filters?.lifecycleStage;
  const sub = filters?.watchSubstatus;
  const from = filters?.handoverDateFrom;
  const to = filters?.handoverDateTo;
  const validStage = VALID_STAGES.includes(stage) ? stage : '';
  // Auto-reset: sub valid only when a stage is selected (any stage)
  const validSub =
    validStage !== '' && VALID_WATCH_SUBS.includes(sub) ? sub : '';
  return {
    searchQuery: typeof filters?.searchQuery === 'string' ? filters.searchQuery.slice(0, 100) : '',
    projectGroup: typeof filters?.projectGroup === 'string' ? filters.projectGroup.slice(0, 50) : '',
    projectType: Array.isArray(filters?.projectType) ? filters.projectType : [],
    teamLead: typeof filters?.teamLead === 'string' ? filters.teamLead.slice(0, 100) : '',
    company: typeof filters?.company === 'string' ? filters.company : '',
    projectMark: typeof filters?.projectMark === 'string' ? filters.projectMark : '',
    projectRoleUserIds: Array.isArray(filters?.projectRoleUserIds) ? filters.projectRoleUserIds : [],
    projectRoleTypes: Array.isArray(filters?.projectRoleTypes) ? filters.projectRoleTypes : [],
    lifecycleStage: validStage,
    watchSubstatus: validSub,
    handoverDateFrom: typeof from === 'string' && ISO_DATE_RE.test(from) ? from : '',
    handoverDateTo: typeof to === 'string' && ISO_DATE_RE.test(to) ? to : '',
    contractYear: Array.isArray(filters?.contractYear) ? filters.contractYear : [],
  };
}


